import type { CanvasItem, ChartOptions } from "@/types/pdfBuilder"

/**
 * 저장된 PDF 템플릿의 옛 아이템을 지금 빌더가 그리는 모양으로 바꾼다 (2026-10-01 결정). 불러올 때 한 번 적용되고,
 * 다시 저장하면 바뀐 모양으로 저장된다.
 *
 * - actionTable(옛 Action Table): Action Map 에 표가 통합됐으므로 지운다
 * - comparison: 팔레트에서 뺀 컴포넌트 — 지우고 그리지 않는다
 * - bar / line / point: Chart by Player(combo)로 — 지표마다 그 종류(bar·line·area·point)를 Series 에 넣어 같은 그림을 유지한다.
 *   옛 가로 막대(orientation=horizontal)는 선수 세로 나열, 옛 dataLabels 는 dataLabel 로
 * - sessionDetails 단일 보기 아이템(intervalViews 없음): 그 보기 하나가 켜진 컨테이너로 — 보기 토글·Layout 을 쓸 수 있게.
 *   옛 4개 묶음의 groupId 는 떼어 낸다(컨테이너의 Manual 묶음과 뜻이 달라 서로 간섭한다)
 * - map(Action Map): 같은 템플릿에 옛 Action Table 이 있었으면 액션 표를 켜고 폭을 가로 최대로, 없었으면 액션 표를 끈다
 *   (2026-10-01 결정 10·11·12)
 * - table: 옛 기본값으로 저장된 장당 행 수 1은 뜻이 없으므로(한 장에 한 행) 지워 지금 기본값(세로 25·가로 16)을 쓰게 한다
 * - 그 밖의 모르는 chartType: 그리지 않고 지운다
 */
const LEGACY_REMOVED_TYPES = new Set(["actionTable", "comparison"])
const KNOWN_TYPES = new Set<string>([
  "activityInfo",
  "combo",
  "activityGraph",
  "point",
  "sessionDetails",
  "scatter",
  "summary",
  "bar",
  "line",
  "table",
  "map",
  "spacer",
])
const GRID_COLUMNS = 12

interface MigrateContext {
  /** 같은 템플릿(페이지)에 옛 Action Table 아이템이 있었는지 */
  hadActionTable: boolean
}
const SESSION_DETAILS_VIEWS = new Set(["session", "1m", "5m", "15m"])

const legacySeriesType = (chartType: string, options: ChartOptions): string => {
  if (chartType === "bar") {
    return "bar"
  }
  if (chartType === "point") {
    return "point"
  }

  return options.chartType === "area" ? "area" : "line"
}

export function migrateTemplateItem(
  item: CanvasItem,
  context: MigrateContext = { hadActionTable: false },
): CanvasItem | null {
  const chartType = item.chartType as string
  if (LEGACY_REMOVED_TYPES.has(chartType) || !KNOWN_TYPES.has(chartType)) {
    return null
  }

  if (chartType === "map") {
    const options: ChartOptions = { ...item.options, showActionTable: context.hadActionTable }

    return context.hadActionTable
      ? { ...item, x: 0, w: GRID_COLUMNS, options }
      : { ...item, options }
  }

  if (chartType === "bar" || chartType === "line" || chartType === "point") {
    const seriesType = legacySeriesType(chartType, item.options)
    const metrics = item.dataSource.metrics ?? []
    const options: ChartOptions = {
      ...item.options,
      metricChartTypes: {
        ...Object.fromEntries(metrics.map((metric) => [metric, seriesType])),
        ...(item.options.metricChartTypes ?? {}),
      },
    }
    if (
      chartType === "bar" &&
      item.options.orientation === "horizontal" &&
      !options.chartOrientation
    ) {
      options.chartOrientation = "vertical"
    }
    if (!options.dataLabel && item.options.dataLabels) {
      options.dataLabel = item.options.dataLabels
    }

    return { ...item, chartType: "combo", options }
  }

  if (chartType === "table" && item.options.rowsPerPage === 1) {
    const options: ChartOptions = { ...item.options }
    delete options.rowsPerPage

    return { ...item, options }
  }

  if (chartType === "sessionDetails" && !Array.isArray(item.options.intervalViews)) {
    const rest: ChartOptions = { ...item.options }
    delete rest.groupId
    const view = item.options.intervalView
    const enabled = view && SESSION_DETAILS_VIEWS.has(view) ? view : "session"

    return { ...item, options: { ...rest, intervalViews: [enabled] } }
  }

  return item
}

export function migrateTemplateItems(items: CanvasItem[]): CanvasItem[] {
  const context: MigrateContext = {
    hadActionTable: items.some((item) => (item.chartType as string) === "actionTable"),
  }

  return items
    .map((item) => migrateTemplateItem(item, context))
    .filter((item): item is CanvasItem => item !== null)
}
